import React, { useContext, useRef, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';
import styled from 'styled-components';
import { Button, Row, Col, Carousel } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import LazyLoad from 'react-lazyload';
import { Title, SubTitle } from '../styles';
import Center from '../../stories/helpers/center';
import { SettingContext } from '../../lib/context/settings';
import wpImageUrl from '../../lib/wp-image-url';
import { device } from '../../lib/theme';
import { useMedia } from '../../utils/hooks';

const SECTIONS_QUERY = gql`
  {
    headerSections(
      first: 4
      where: { status: PUBLISH, orderby: { field: MODIFIED, order: ASC } }
    ) {
      nodes {
        id
        title
        content
        featuredImage {
          node {
            mediaItemUrl
          }
        }
      }
    }
  }
`;

export const HeroContainer = styled(Row)`
  height: 420px;
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  margin: 0 !important;
  h1 {
    max-width: 80%;
    font-size: 32px;
    line-height: 1.1;
  }
  .hero-sub-title {
    font-size: 18px;
    line-height: 1.1;
  }

  @media ${device.laptop} {
    h1 {
      max-width: 60%;
      font-size: 56px;
      line-height: 1.2;
    }
    .hero-sub-title {
      font-size: 28px;
      line-height: 1.1;
    }
    height: 660px;
  }
`;

export const HeroBtnGroup = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin-top: 32px;

  .ant-btn {
    min-width: 250px;
    min-height: 50px;
    margin-bottom: 16px;
  }
  .withSubText {
    display: flex;
    flex-flow: column nowrap;
  }
  button.ant-btn > .subtext {
    font-size: 10px;
    font-weight: 700;
    color: #cd1227;
    line-height: 1;
  }

  @media ${device.tablet} {
    flex-flow: row nowrap;
    button {
      margin-bottom: 0;
    }
  }
`;

export const HeroNavContainer = styled.div`
  margin-top: -80px;
  margin-bottom: 10px;
  position: relative;
  z-index: 5;
  height: 70px;
  background-color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeroNavButton = styled.button`
  border: 0;
  background-color: transparent;
  color: ${({ selected }) => (selected ? '#CD2127' : '#343434')};
  ${({ selected }: { selected: boolean }) =>
    selected ? 'text-decoration: underline' : null};
  font-family: 'CG-regular';
  font-size: 18px;
  font-weight: 700;
  border-right: 1px solid #aaa;
  height: 40px;
  flex: 1;
  cursor: pointer;
  outline: none;

  :last-of-type {
    border-right: none;
  }
`;

const HeroCarousel = styled(Carousel)`
  .slick-dots-bottom {
    bottom: 20px;

    @media ${device.laptop} {
      bottom: 80px;
    }
  }
`;

const Hero: React.FC<{ nonmember?: boolean }> = ({ nonmember = false }) => {
  const { setSetting, settings } = useContext(SettingContext);
  const slider = useRef<any>(null);
  const [autoPlay, setAutoPlay] = useState(true);
  const [current, setCurrent] = useState(0);
  const { loading, error, data } = useQuery(SECTIONS_QUERY);
  const sections = data
    ? data.headerSections.nodes.map((node: any) => ({
        id: node.id,
        title: node.title,
        content: node.content,
        img: wpImageUrl(node?.featuredImage?.node?.mediaItemUrl || ''),
      }))
    : [];

  const goTo = (slide: number) => () => {
    if (slider.current !== null) {
      slider.current.goTo(slide);
    }
    setAutoPlay(false);
  };

  const hideHeroNavButtons = useMedia([`(max-width: 769px)`], [true], false);

  const handleBeforeChange = (_: any, current: number) => {
    setCurrent(current);
  };

  const goToMemberView = () => {
    setSetting('member', !settings.member);
  };

  if (nonmember) {
    return (
      <HeroContainer
        align="middle"
        gutter={[0, 16]}
        style={{ backgroundImage: 'url(/img/hero/hero-spring2021.jpg)' }}
      >
        <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 2 }}>
          <Title>Expect Something More</Title>
          <SubTitle>AAA is with you wherever you go.</SubTitle>

          <HeroBtnGroup>
            <Button
              size="large"
              type="primary"
              style={{ marginRight: 16 }}
              href="https://join.carolinas.aaa.com/?promo=INTRNET"
            >
              Join Now
            </Button>
            <Button
              size="large"
              type="default"
              onClick={goToMemberView}
              className="withSubText ctaAlreadyMember"
            >
              Already A Member?
              <div className="subtext">Continue to Member Site</div>
            </Button>
          </HeroBtnGroup>
        </Col>
      </HeroContainer>

      // <HeroContainer
      //   align="middle"
      //   gutter={[0, 16]}
      //   style={{
      //     backgroundImage: 'url(/img/hero/MS-1385-balloons-2000x1182-min.jpg)',
      //   }}
      // >
      //   <Col xs={{ span: 22, offset: 1 }} lg={{ span: 20, offset: 2 }}>
      //     <Title white>Join Today and Save 50% on Membership</Title>
      //     <SubTitle white style={{ marginTop: 16 }}>
      //       Hurry,&nbsp;Offer&nbsp;Ends&nbsp;9/30!
      //     </SubTitle>

      //     <HeroBtnGroup>
      //       <Button
      //         href="https://join.carolinas.aaa.com/?promo=HALFPRB&utm_source=br&utm_medium=w&utm_campaign=memb-acq-prime-day-50"
      //         size="large"
      //         type="primary"
      //         style={{ marginRight: 16 }}
      //       >
      //         Get Offer
      //       </Button>
      //       <Button
      //         size="large"
      //         type="default"
      //         onClick={goToMemberView}
      //         className="withSubText ctaAlreadyMember"
      //       >
      //         Already A Member?
      //         <div className="subtext">Continue to Member Site</div>
      //       </Button>
      //     </HeroBtnGroup>
      //   </Col>
      // </HeroContainer>
    );
  }

  return (
    <>
      <HeroCarousel
        autoplay={autoPlay}
        lazyLoad="progressive"
        ref={slider}
        dotPosition="bottom"
        beforeChange={handleBeforeChange}
        autoplaySpeed={6000}
        speed={1500}
      >
        <div>
          {/* <HeroContainer
            align="middle"
            gutter={[0, 16]}
            style={{ backgroundImage: 'url(/img/hero/hero-spring2021.jpg)' }}
          >
            <Col xs={{ span: 22, offset: 1 }} lg={{ span: 20, offset: 2 }}>
              <Title>Expect Something More</Title>
              <SubTitle>AAA is with you wherever you go.</SubTitle>

              <HeroBtnGroup>
                <Button
                  href="https://carolinas.aaa.com/membership/plans/"
                  size="large"
                  type="primary"
                  style={{ marginRight: 16 }}
                >
                  Upgrade Now
                </Button>
                <Button
                  size="large"
                  type="default"
                  href="https://carolinas.aaa.com/membership/renew/"
                >
                  Renew Membership
                </Button>
              </HeroBtnGroup>
            </Col>
          </HeroContainer> */}

          <HeroContainer
            align="middle"
            gutter={[0, 16]}
            style={{
              backgroundImage:
                'url(/img/hero/MS-1385-balloons-2000x1182-min.jpg)',
            }}
          >
            <Col xs={{ span: 22, offset: 1 }} lg={{ span: 20, offset: 2 }}>
              <Title white>Join Today and Save 50% on Membership</Title>
              <SubTitle white style={{ marginTop: 16 }}>
                Hurry,&nbsp;Offer&nbsp;Ends&nbsp;9/30!
              </SubTitle>

              <HeroBtnGroup>
                <Button
                  href="https://join.carolinas.aaa.com/?promo=HALFPRB&utm_source=br&utm_medium=w&utm_campaign=memb-acq-prime-day-50"
                  size="large"
                  type="primary"
                >
                  Get Offer
                </Button>
              </HeroBtnGroup>
            </Col>
          </HeroContainer>
        </div>
        {loading ? (
          <div>
            {' '}
            <HeroContainer>
              <Center>
                <LoadingOutlined style={{ fontSize: 40 }} />
              </Center>
            </HeroContainer>
          </div>
        ) : null}
        {error ? (
          <div>
            <HeroContainer>
              <Center>Please disable Ad Blockers.</Center>
            </HeroContainer>
          </div>
        ) : null}
        {sections.map(({ id, content, img }) => (
          <div key={id}>
            <LazyLoad once height={660}>
              <HeroContainer
                align="middle"
                gutter={[0, 16]}
                style={{ backgroundImage: `url(${img})` }}
              >
                <Col xs={{ span: 22, offset: 1 }} lg={{ span: 20, offset: 2 }}>
                  <div dangerouslySetInnerHTML={{ __html: content }} />
                </Col>
              </HeroContainer>
            </LazyLoad>
          </div>
        ))}
      </HeroCarousel>

      {!hideHeroNavButtons ? (
        <Col xs={{ span: 22, offset: 1 }} lg={{ span: 20, offset: 2 }}>
          <HeroNavContainer>
            <HeroNavButton selected={current === 0} onClick={goTo(0)}>
              Membership Offer Ends Soon
            </HeroNavButton>
            {sections.map(({ id, title }, idx) => (
              <HeroNavButton
                key={id}
                selected={current === idx + 1}
                onClick={goTo(idx + 1)}
              >
                {title}
              </HeroNavButton>
            ))}
          </HeroNavContainer>
        </Col>
      ) : null}
    </>
  );
};

export default Hero;
